/**
 * FillTheGapModal — in-place "I don't have this — how do I fill it?" modal.
 *
 * Opens over the page when the user clicks the gap-CTA on a requirement row.
 * Surfaces the most relevant courses from the user's program catalog that
 * could close the gap, plus a (no-op for now) "Book an appointment with an
 * Advisor" affordance.
 *
 *   ┌─ How to fill this gap · Senior PMM ─────────────[X]─┐
 *   │ "Pricing and packaging strategy input"             │
 *   │ Fit ●●●○○ · target 3 / you 1                       │
 *   ├─────────────────────────────────────────────────────┤
 *   │ Curated from your UW Foster catalog · 3 matches    │
 *   │                                                     │
 *   │ ┌─────────────────────────────────────────────────┐│
 *   │ │ MKTG 568 · MBA · 4 cr                           ││
 *   │ │ Pricing Strategy and Analytics                  ││
 *   │ │ Why this fits: pricing · strategy · analytics   ││
 *   │ │ "Blends marketing analytic frameworks…"         ││
 *   │ │                            [Add to my plan →]   ││
 *   │ └─────────────────────────────────────────────────┘│
 *   │ … more courses …                                    │
 *   ├─────────────────────────────────────────────────────┤
 *   │ Not sure what to take?                              │
 *   │ [📅 Book an appointment with an Advisor]            │
 *   │ [Browse the full catalog →]                         │
 *   └─────────────────────────────────────────────────────┘
 *
 * Catalog is loaded from a static JSON in /src/data — Foster MBA grad
 * courses scoped down to ~130 entries relevant to a marketing pivot.
 * Real catalog wiring + a real recommendation engine come later.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Dialog,
  IconButton,
  Snackbar,
  Alert,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { SAGE, CREAM, WARM } from '../../theme';
import { TargetDots } from '../jobs/RequirementRow';
import COURSES from '../../data/foster_courses.json';

const F = "'Outfit', sans-serif";
const SERIF = "'Lora', Georgia, serif";

const STOP_WORDS = new Set([
  'the','and','for','with','from','into','that','this','your','you','have',
  'into','onto','upon','about','their','them','they','what','when','where',
  'which','will','would','should','could','these','those','being','been',
  'across','through','within','also','more','than','then','some','such','each',
  'any','all','our','its','was','were','are','can','may','use','using','used',
  'over','tier','one','two','three','many','make','makes','made','build','builds',
  'built','plus','etc','really','very','well','versus','vs','via',
]);

const tokenize = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 3 && !STOP_WORDS.has(w));

const dedupe = (arr) => Array.from(new Set(arr));

const scoreCourse = (course, queryTokens) => {
  if (!queryTokens.length) return { score: 0, hits: [] };
  const titleTokens = new Set(tokenize(course.title));
  const descTokens = new Set(tokenize(course.description));
  let score = 0;
  const hits = [];
  for (const t of queryTokens) {
    if (titleTokens.has(t)) {
      score += 3;
      hits.push(t);
    } else if (descTokens.has(t)) {
      score += 1;
      hits.push(t);
    }
  }
  return { score, hits: dedupe(hits) };
};

const matchCourses = (requirement, limit = 4) => {
  if (!requirement) return [];
  const queryText = [
    requirement.text,
    ...(requirement.wordings || []),
  ].join(' ');
  const queryTokens = dedupe(tokenize(queryText));
  const scored = COURSES
    .map((c) => ({ course: c, ...scoreCourse(c, queryTokens) }))
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score);
  // If the requirement looks marketing-related at all and we have nothing,
  // fall back to MKTG core grad picks so the modal isn't empty.
  if (scored.length === 0) {
    const fallback = COURSES.filter((c) => c.code.startsWith('MKTG 5')).slice(0, limit);
    return fallback.map((c) => ({ course: c, score: 0, hits: [] }));
  }
  return scored.slice(0, limit);
};

const FillTheGapModal = ({ open, onClose, requirement, roleContext }) => {
  const navigate = useNavigate();
  const matches = useMemo(() => matchCourses(requirement, 4), [requirement]);
  const [snack, setSnack] = useState(null);

  const goToCourse = (code) => {
    onClose();
    navigate(`/courses/${encodeURIComponent(code)}`);
  };
  const goToCatalog = () => {
    onClose();
    navigate('/courses');
  };

  if (!requirement) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { bgcolor: CREAM[50], borderRadius: 3, maxHeight: '90vh' },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3, pt: 2.5, pb: 2,
            borderBottom: `1px solid ${SAGE[200]}`,
            display: 'flex', alignItems: 'flex-start', gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: F, fontSize: '0.65rem', fontWeight: 700,
                letterSpacing: 1.6, textTransform: 'uppercase', color: SAGE[500], mb: 0.5,
              }}
            >
              How to fill this gap
              {roleContext?.role ? ` · ${roleContext.role}` : ''}
              {roleContext?.level ? ` · ${roleContext.level}` : ''}
            </Typography>
            <Typography
              sx={{
                fontFamily: SERIF, fontSize: '1.15rem', fontWeight: 500,
                color: SAGE[800], lineHeight: 1.35,
              }}
            >
              {requirement.text}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: SAGE[500], mt: 0.25 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Context strip */}
        <Box
          sx={{
            px: 3, py: 1.75,
            borderBottom: `1px solid ${SAGE[100]}`,
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
            bgcolor: `${SAGE[100]}55`,
          }}
        >
          {requirement.target != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontFamily: F, fontSize: '0.7rem', color: SAGE[500], fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                Fit
              </Typography>
              <TargetDots required={requirement.target} has={requirement.has || 0} />
              <Typography sx={{ fontFamily: F, fontSize: '0.72rem', color: SAGE[500] }}>
                {(requirement.has || 0) === 0
                  ? `target ${requirement.target} · nothing in your profile yet`
                  : `target ${requirement.target} · you have ${requirement.has}`}
              </Typography>
            </Box>
          )}
          {requirement.category && (
            <Box
              sx={{
                fontFamily: F, fontSize: '0.62rem', fontWeight: 700,
                letterSpacing: 0.4, textTransform: 'uppercase',
                px: 1, py: 0.25, borderRadius: 1,
                bgcolor: SAGE[200], color: SAGE[700],
              }}
            >
              {requirement.category}
            </Box>
          )}
        </Box>

        {/* Catalog matches */}
        <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon sx={{ fontSize: 16, color: SAGE[500] }} />
          <Typography sx={{ fontFamily: F, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: SAGE[500] }}>
            From your UW Foster catalog · {matches.length} {matches.length === 1 ? 'match' : 'matches'}
          </Typography>
        </Box>

        <Box sx={{ px: 3, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', flex: 1 }}>
          {matches.length === 0 ? (
            <Typography sx={{ fontFamily: F, fontSize: '0.85rem', color: SAGE[500], fontStyle: 'italic', py: 2 }}>
              No catalog matches surfaced. Try the Advisor route or browse the full catalog.
            </Typography>
          ) : (
            matches.map((m) => (
              <CourseCard
                key={m.course.code}
                course={m.course}
                hits={m.hits}
                onAdd={() => goToCourse(m.course.code)}
              />
            ))
          )}
        </Box>

        {/* Advisor CTA footer */}
        <Box
          sx={{
            px: 3, py: 2.5,
            borderTop: `1px solid ${SAGE[200]}`,
            bgcolor: `${CREAM[100]}`,
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontFamily: SERIF, fontSize: '0.95rem', fontWeight: 500, color: SAGE[800], mb: 0.25 }}>
              Not sure what to take?
            </Typography>
            <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[500], fontStyle: 'italic' }}>
              An advisor can map your gaps against the full Foster catalog — including cross-listed and special-topics courses.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              onClick={() => setSnack({ text: 'Advisor scheduling — coming soon.', severity: 'info' })}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.85,
                px: 1.75, py: 1, borderRadius: 1.5,
                bgcolor: SAGE[700], color: CREAM[50], cursor: 'pointer',
                fontSize: '0.78rem', fontWeight: 600, fontFamily: F,
                transition: 'background-color 0.18s',
                '&:hover': { bgcolor: SAGE[800] },
              }}
            >
              <EventAvailableIcon sx={{ fontSize: 16 }} />
              Book an appointment with an Advisor
            </Box>
            <Box
              onClick={goToCatalog}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                px: 1.25, py: 1, borderRadius: 1.5,
                color: SAGE[600], cursor: 'pointer',
                fontSize: '0.74rem', fontWeight: 600, fontFamily: F,
                '&:hover': { color: SAGE[800] },
              }}
            >
              Browse the full catalog
              <OpenInNewIcon sx={{ fontSize: 13 }} />
            </Box>
          </Box>
        </Box>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack && (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} sx={{ width: '100%' }}>
            {snack.text}
          </Alert>
        )}
      </Snackbar>
    </>
  );
};

const CourseCard = ({ course, hits, onAdd }) => {
  const quarters = (course.quartersOffered || []).join(' · ') || null;
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: 2,
        border: `1.5px solid ${SAGE[200]}`,
        p: 2,
        display: 'flex', flexDirection: 'column', gap: 0.75,
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:hover': { borderColor: SAGE[400], boxShadow: `0 2px 8px ${SAGE[200]}55` },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography
          sx={{
            fontFamily: F, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5,
            color: SAGE[700], bgcolor: SAGE[100], px: 0.85, py: 0.25, borderRadius: 0.75,
          }}
        >
          {course.code}
        </Typography>
        <Typography
          sx={{
            fontFamily: F, fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.5,
            textTransform: 'uppercase', color: SAGE[500], bgcolor: SAGE[100], px: 0.85, py: 0.25, borderRadius: 0.75,
          }}
        >
          {course.level} · {course.credits} cr
        </Typography>
        {quarters && (
          <Typography
            sx={{
              fontFamily: F, fontSize: '0.7rem', fontWeight: 500,
              color: SAGE[500], fontStyle: 'italic',
            }}
          >
            offered: {quarters}
          </Typography>
        )}
      </Box>
      <Typography
        sx={{
          fontFamily: SERIF, fontSize: '1.02rem', fontWeight: 500,
          color: SAGE[800], lineHeight: 1.3,
        }}
      >
        {course.title}
      </Typography>
      {hits && hits.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: SAGE[500] }}>
            why this fits
          </Typography>
          {hits.slice(0, 5).map((h) => (
            <Box
              key={h}
              sx={{
                fontFamily: F, fontSize: '0.7rem', fontWeight: 600,
                color: '#3d7a4a', bgcolor: '#3d7a4a18',
                px: 0.85, py: 0.15, borderRadius: 0.75,
              }}
            >
              {h}
            </Box>
          ))}
        </Box>
      )}
      <Typography
        sx={{
          fontFamily: F, fontSize: '0.82rem', color: SAGE[600], lineHeight: 1.5,
        }}
      >
        {course.description}
      </Typography>
      {course.reviews && course.reviews.length > 0 && (
        <Typography
          sx={{
            fontFamily: SERIF, fontSize: '0.78rem', fontStyle: 'italic',
            color: SAGE[500], lineHeight: 1.5, mt: 0.5,
          }}
        >
          "{course.reviews[0].text}"
          <Box component="span" sx={{ fontFamily: F, fontSize: '0.7rem', color: SAGE[400], ml: 0.5, fontStyle: 'normal' }}>
            — {course.reviews[0].reviewer}
          </Box>
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
        <Box
          onClick={onAdd}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            px: 1.25, py: 0.75, borderRadius: 1.5,
            bgcolor: 'transparent', color: WARM.accent,
            border: `1.5px solid ${WARM.accent}50`, cursor: 'pointer',
            fontSize: '0.72rem', fontWeight: 700, fontFamily: F,
            letterSpacing: 0.3, textTransform: 'uppercase',
            transition: 'all 0.18s',
            '&:hover': { bgcolor: `${WARM.accent}15`, borderColor: WARM.accent },
          }}
        >
          View course →
        </Box>
      </Box>
    </Box>
  );
};

export default FillTheGapModal;
